<script setup lang="ts">
import { computed } from 'vue'
import { Collapsible, TheTooltip, formatNumber } from '@wastedondestiny/destiny-library'
import collectibles from '../generated/collectibleDefinitions.json'
import MemberIndicator from './MemberIndicator.vue'
import { CollectionAccount } from '../types'
import ItemIcon from './ItemIcon.vue'
import { ExoticSourceExpansion, ExoticWeapon } from '../enums/releases'

const emit = defineEmits(['toggle'])
const props = defineProps<{
  exotic: ExoticWeapon,
  members: CollectionAccount[],
  isOpen: { missing: boolean, acquired: boolean, unavailable: boolean }
}>()
const now = Date.now()

function isActive(date: Date) {
  return (now - date.getTime()) < 2_629_743_000 // Milliseconds in a month
}

function checkOwnership(member: CollectionAccount) {
  return !props.exotic.ownership?.length ||
    props.exotic.ownership.some(y => {
      const hasExpansion = ((member.versionsOwned ?? 0) & y) === y
      const hasSeason = member.seasonsOwned?.includes(y)
      const hasDungeon = member.dungeonsOwned?.includes(y)
      return hasExpansion || hasSeason || hasDungeon
    })
}

const activeMembers = computed(() => props.members.filter(x => checkOwnership(x) && isActive(x.dateLastPlayed!)))
const inactiveMembers = computed(() => props.members.filter(x => !checkOwnership(x) || !isActive(x.dateLastPlayed!)))

const activeMembersWhoDontHave = computed(() => activeMembers.value.filter(x => !x.items[props.exotic.collectible]))
const activeMembersWhoHave = computed(() => activeMembers.value.filter(x => x.items[props.exotic.collectible]))

const inactiveMembersWhoDontHave = computed(() => inactiveMembers.value.filter(x => checkOwnership(x) && !x.items[props.exotic.collectible]))
const inactiveMembersWhoHave = computed(() => inactiveMembers.value.filter(x => x.items[props.exotic.collectible]))

const membersWhoCantHave = computed(() => inactiveMembers.value.filter(x => !checkOwnership(x) && !x.items[props.exotic.collectible]))

const ownershipPercentage = computed(() => props.members.filter(x => x.items[props.exotic.collectible]).length / props.members.length)

const collectible = computed(() => collectibles.find(x => x.id === props.exotic.collectible))

function toggle(tag: 'missing'|'acquired'|'unavailable') {
  emit('toggle', tag)
}
</script>

<template>
  <div class="flex flex-col w-72 bg-white text-black shadow-lg shadow-black/50 rounded-2xl px-2 py-3 mb-5 relative">
    <div class="flex items-center justify-between -mt-3 mb-1 w-72 -mx-2 rounded-t-2xl py-1 px-2 bg-zinc-200">
      <div class="flex gap-1">
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.RedWar_World">Red War World Drop</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.RedWar_Archive">Red War Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Forsaken_World">Forsaken World Drop</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Forsaken_Archive">Forsaken Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Forsaken_Quest">Forsaken Exotic Quest</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Shadowkeep_World">Shadowkeep World Drop</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Shadowkeep_Archive">Shadowkeep Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Shadowkeep_Quest">Shadowkeep Exotic Quest</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.BeyondLight_Archive">Beyond Light Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.BeyondLight_Quest">Beyond Light Exotic Quest</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.BeyondLight_World">Beyond Light World Drop</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.WitchQueen_Archive">The Witch Queen Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.WitchQueen_Quest">The Witch Queen Exotic Quest</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Lightfall_Archive">Lightfall Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Lightfall_Quest">Lightfall Exotic Quest</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.FinalShape_Archive">The Final Shape Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.FinalShape_Quest">The Final Shape Exotic Quest</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.EdgeOfFate_Archive">Edge of Fate Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.EdgeOfFate_Quest">Edge of Fate Exotic Quest</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Renegades_Archive">Renegades Exotic Archive</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Renegades_Season">Renegades Reward Pass</span>
        <span class="uppercase text-xs" v-if="exotic.source === ExoticSourceExpansion.Event_Quest">Event Exotic Quest</span>
      </div>
      <span class="h-4 leading-3" v-if="members.length">
        <span class="text-xs"><small class="opacity-75">HAS:</small>{{ formatNumber(Math.floor(ownershipPercentage * 100)) }}%</span>
      </span>
    </div>
    <div class="pb-1 mb-1 border-b-2 border-black/25">
      <TheTooltip>
        <ItemIcon :item="collectible" large />
        <span class="ml-[3.75rem] mb-1 inline-flex flex-col">
          <h2 class="text-xl font-semibold">{{ collectible?.name }}</h2>
          <small class="text-sm">{{ collectible?.type }}</small>
        </span>
        <template #icon>
          <ItemIcon :item="collectible" />
        </template>
        <template #content>
          <p>{{ exotic.description }}</p>
        </template>
      </TheTooltip>
    </div>
    <div class="flex flex-col py-1" v-if="members.length">
      <Collapsible name="MISSING THE EXOTIC" :open="isOpen['missing']" @toggle="() => toggle('missing')">
        <MemberIndicator v-for="member in activeMembersWhoDontHave" :key="member.membershipId" :account="member">
          {{ member.displayName }}
        </MemberIndicator>
      </Collapsible>
      <Collapsible name="HAVE THE EXOTIC" :open="isOpen['acquired']" @toggle="() => toggle('acquired')">
        <MemberIndicator v-for="member in activeMembersWhoHave" :key="member.membershipId" :account="member" has-exotic>
          {{ member.displayName }}
        </MemberIndicator>
      </Collapsible>
      <Collapsible name="INACTIVE MEMBERS" :open="isOpen['unavailable']" @toggle="() => toggle('unavailable')">
        <MemberIndicator v-for="member in inactiveMembersWhoDontHave" :key="member.membershipId" :account="member" no-count>
          {{ member.displayName }}
        </MemberIndicator>
        <MemberIndicator v-for="member in membersWhoCantHave" :key="member.membershipId" :account="member" has-no-access no-count>
          {{ member.displayName }}
        </MemberIndicator>
        <MemberIndicator v-for="member in inactiveMembersWhoHave" :key="member.membershipId" :account="member" has-exotic :has-no-access="!checkOwnership(member)" no-count>
          {{ member.displayName }}
        </MemberIndicator>
      </Collapsible>
    </div>
  </div>
</template>